import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  increment,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { getFirebaseClients } from '../firebase';

/**
 * Lightweight engagement counters surfaced in the admin dashboard.
 *
 * Two storage shapes are written on every hit:
 *   1. CUMULATIVE (all-time), 2 docs — cheap "Tout" reads:
 *        analytics/formationViews → { counts: { <slug>: number } }
 *        analytics/events         → { counts: { <eventName>: number } }
 *   2. DAILY BUCKETS — one doc per calendar day, enables day/week/month/year
 *      filtering by summing the buckets in range:
 *        analytics_daily/{YYYY-MM-DD} → { views: { <slug>: n }, events: { <name>: n } }
 *
 * Doc IDs are local-date `YYYY-MM-DD`, lexicographically sortable, so a range
 * is one indexed query on documentId(). Public visitors may write (fire-and-
 * forget); only admins may read (firestore.rules). GA4 covers visitor/source/
 * funnel — this powers the in-admin "what's hot, and when" panel.
 */

const COLLECTION = 'analytics';
const DAILY_COLLECTION = 'analytics_daily';
const VIEWS_DOC = 'formationViews';
const EVENTS_DOC = 'events';
const EVENEMENT_CLICKS_DOC = 'evenementClicks';

export type AnalyticsCounts = Record<string, number>;
export type AnalyticsBundle = {
  views: AnalyticsCounts;
  events: AnalyticsCounts;
  /** Clicks on « S'inscrire à l'événement », keyed by event slug. */
  evenementInscription: AnalyticsCounts;
  /** Clicks on the Home « vedette » event block, keyed by event slug. */
  evenementVedette: AnalyticsCounts;
};

function db() {
  return getFirebaseClients().firestore;
}

/** Local-date key `YYYY-MM-DD` for the given date (defaults to now). */
function dateKey(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Increment the view counter for one formation (cumulative + today's bucket). */
export async function recordFormationView(slug: string): Promise<void> {
  if (!slug) return;
  const today = dateKey();
  await Promise.all([
    setDoc(
      doc(db(), COLLECTION, VIEWS_DOC),
      { counts: { [slug]: increment(1) }, updatedAt: serverTimestamp() },
      { merge: true },
    ),
    setDoc(
      doc(db(), DAILY_COLLECTION, today),
      { views: { [slug]: increment(1) }, updatedAt: serverTimestamp() },
      { merge: true },
    ),
  ]);
}

/**
 * Increment an event-click counter, keyed by the event slug, split by kind:
 *   - 'inscription' → click on « S'inscrire à l'événement » (detail page CTA)
 *   - 'vedette'     → click on the Home featured-event block
 * Cumulative doc `analytics/evenementClicks { inscription:{slug:n}, vedette:{slug:n} }`
 * + daily bucket `analytics_daily/{day} { evInscription:{slug:n}, evVedette:{slug:n} }`.
 */
export async function recordEvenementClick(
  kind: 'inscription' | 'vedette',
  slug: string,
): Promise<void> {
  if (!slug) return;
  const today = dateKey();
  const dailyKey = kind === 'inscription' ? 'evInscription' : 'evVedette';
  await Promise.all([
    setDoc(
      doc(db(), COLLECTION, EVENEMENT_CLICKS_DOC),
      { [kind]: { [slug]: increment(1) }, updatedAt: serverTimestamp() },
      { merge: true },
    ),
    setDoc(
      doc(db(), DAILY_COLLECTION, today),
      { [dailyKey]: { [slug]: increment(1) }, updatedAt: serverTimestamp() },
      { merge: true },
    ),
  ]);
}

/** Increment a named engagement event (cumulative + today's bucket). */
export async function recordEvent(name: string): Promise<void> {
  if (!name) return;
  const today = dateKey();
  await Promise.all([
    setDoc(
      doc(db(), COLLECTION, EVENTS_DOC),
      { counts: { [name]: increment(1) }, updatedAt: serverTimestamp() },
      { merge: true },
    ),
    setDoc(
      doc(db(), DAILY_COLLECTION, today),
      { events: { [name]: increment(1) }, updatedAt: serverTimestamp() },
      { merge: true },
    ),
  ]);
}

/** Admin read — all-time cumulative view + event + event-click counts (3 reads). */
export async function getAllTimeAnalytics(): Promise<AnalyticsBundle> {
  const [vSnap, eSnap, evSnap] = await Promise.all([
    getDoc(doc(db(), COLLECTION, VIEWS_DOC)),
    getDoc(doc(db(), COLLECTION, EVENTS_DOC)),
    getDoc(doc(db(), COLLECTION, EVENEMENT_CLICKS_DOC)),
  ]);
  const views = (vSnap.data() as { counts?: AnalyticsCounts } | undefined)?.counts ?? {};
  const events = (eSnap.data() as { counts?: AnalyticsCounts } | undefined)?.counts ?? {};
  const ev = evSnap.data() as
    | { inscription?: AnalyticsCounts; vedette?: AnalyticsCounts }
    | undefined;
  return {
    views,
    events,
    evenementInscription: ev?.inscription ?? {},
    evenementVedette: ev?.vedette ?? {},
  };
}

/**
 * Admin read — sum the daily buckets over the last `days` days (inclusive of
 * today). One indexed query on documentId() ≥ startKey.
 */
export async function getAnalyticsRange(days: number): Promise<AnalyticsBundle> {
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  const startKey = dateKey(start);

  const q = query(collection(db(), DAILY_COLLECTION), where(documentId(), '>=', startKey));
  const snap = await getDocs(q);

  const views: AnalyticsCounts = {};
  const events: AnalyticsCounts = {};
  const evenementInscription: AnalyticsCounts = {};
  const evenementVedette: AnalyticsCounts = {};
  const add = (target: AnalyticsCounts, src: AnalyticsCounts | undefined) => {
    for (const [k, v] of Object.entries(src ?? {})) {
      target[k] = (target[k] ?? 0) + (typeof v === 'number' ? v : 0);
    }
  };
  snap.forEach((d) => {
    const data = d.data() as {
      views?: AnalyticsCounts;
      events?: AnalyticsCounts;
      evInscription?: AnalyticsCounts;
      evVedette?: AnalyticsCounts;
    };
    add(views, data.views);
    add(events, data.events);
    add(evenementInscription, data.evInscription);
    add(evenementVedette, data.evVedette);
  });
  return { views, events, evenementInscription, evenementVedette };
}

// Back-compat single-shape readers (used elsewhere if needed).
export async function getFormationViews(): Promise<AnalyticsCounts> {
  return (await getAllTimeAnalytics()).views;
}
export async function getEventCounts(): Promise<AnalyticsCounts> {
  return (await getAllTimeAnalytics()).events;
}
